import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowUpRight, Download, RefreshCw, ShieldAlert, X } from 'lucide-react';
import type { UpdateSourcePreference, UpdateState } from '../../shared/types';

interface UpdateDialogProps {
  open: boolean;
  appName: string;
  updateState: UpdateState;
  busyAction: 'check' | 'install' | null;
  downloadCenterUrl: string;
  onOpenChange: (open: boolean) => void;
  onCheckForUpdates: () => void;
  onInstallUpdate: () => void;
  onOpenNotes: () => void;
  onOpenHelp: () => void;
  onOpenExternal: (url: string) => void;
}

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '未记录';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '未记录';
  }

  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const mapUpdateSourcePreferenceLabel = (sourcePreference: UpdateSourcePreference): string => {
  switch (sourcePreference) {
    case 'github':
      return 'GitHub';
    case 'gitee':
      return 'Gitee';
    default:
      return '自动';
  }
};

const getUpdateChannelBadgeLabel = (channel: UpdateState['channel']): string | null => {
  switch (channel) {
    case 'beta':
      return '内测';
    case 'canary':
      return '测试';
    default:
      return null;
  }
};

const formatBytes = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) {
    return '0 B';
  }

  if (value >= 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  }

  if (value >= 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }

  return `${value} B`;
};

export function UpdateDialog({
  open,
  appName,
  updateState,
  busyAction,
  downloadCenterUrl,
  onOpenChange,
  onCheckForUpdates,
  onInstallUpdate,
  onOpenHelp,
  onOpenExternal,
}: UpdateDialogProps) {
  const [sourceSwitchingTo, setSourceSwitchingTo] = useState<UpdateSourcePreference | null>(null);
  const [sourceSwitchMessage, setSourceSwitchMessage] = useState<string | null>(null);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const isMandatory = updateState.blockedByMandatory;
  const updateTitle = '发现新版本';
  const isError = updateState.availability === 'error';
  const isChecking = updateState.availability === 'checking';
  const isUpToDate = updateState.availability === 'up-to-date';
  const isDownloading = updateState.availability === 'downloading';
  const hasDownloadedInstaller =
    updateState.currentPlatform === 'win' && Boolean(updateState.downloadFilePath);
  const isInstallerLaunchError = isError && hasDownloadedInstaller;
  const notesContent =
    updateState.notesContent ||
    updateState.manifest?.notes ||
    updateState.errorMessage ||
    '当前还没有拿到更新说明。';
  const progressVisible =
    updateState.downloadStage !== 'idle' || isDownloading || updateState.availability === 'ready';
  const primaryActionLabel =
    hasDownloadedInstaller
      ? '开始安装更新'
      : updateState.currentPlatform === 'win'
        ? '立即更新'
        : '下载更新包';
  const progressPercentLabel =
    updateState.downloadProgressPercent !== null
      ? `${updateState.downloadProgressPercent}%`
      : updateState.downloadStage === 'verifying'
        ? '校验中'
        : updateState.downloadStage === 'ready'
          ? '100%'
          : updateState.downloadStage === 'failed'
            ? '失败'
            : '进行中';
  const progressMetaLabel = updateState.downloadTotalBytes
    ? `${formatBytes(updateState.downloadTransferredBytes)} / ${formatBytes(updateState.downloadTotalBytes)}`
    : `${formatBytes(updateState.downloadTransferredBytes)} / 总大小未知`;
  const sourceControlsDisabled =
    sourceSwitchingTo !== null || busyAction !== null || isChecking || isDownloading;
  const channelBadgeLabel = getUpdateChannelBadgeLabel(updateState.channel);
  const officialDownloadUrl = downloadCenterUrl;
  const dialogTitle = appName;
  const dialogDescription = updateState.updateAvailable
    ? isMandatory
      ? '当前版本已经低于允许继续使用的范围，请先完成更新。'
      : '已检测到可安装的新版本。'
    : isInstallerLaunchError
      ? '安装包还保留在本地，你可以再次点击“开始安装更新”重试；安装器会提示关闭当前软件。'
    : isError
      ? '本次更新流程没有完成，下面会显示失败原因和最近一次状态。'
      : isUpToDate
        ? '当前版本已经完成检查。'
        : isChecking
          ? '正在连接更新源并校验清单签名，请稍候。'
          : '这里会展示最新版本、发布时间和更新说明摘要。';
  const bannerTitle = updateState.updateAvailable
    ? updateTitle
    : isInstallerLaunchError
      ? '启动安装器失败'
    : isError
      ? '更新失败'
      : isUpToDate
        ? '当前软件已是最新'
        : isChecking
          ? '正在检查更新'
          : '尚未开始检查';
  const bannerSummary = isInstallerLaunchError
    ? '安装器已保留在本地，可重新发起安装。'
    : isError
      ? (updateState.errorMessage ?? '本次更新未完成。')
      : null;

  useEffect(() => {
    if (!open) {
      setSourceSwitchingTo(null);
      setSourceSwitchMessage(null);
      setNotesExpanded(false);
    }
  }, [open]);

  async function handleSwitchSourcePreference(nextPreference: UpdateSourcePreference) {
    const nextLabel = mapUpdateSourcePreferenceLabel(nextPreference);
    setSourceSwitchingTo(nextPreference);
    setSourceSwitchMessage(`正在切到 ${nextLabel}，并重新检查更新...`);

    try {
      if (typeof window.codexWorkspace.setUpdateSourcePreference !== 'function') {
        setSourceSwitchMessage('当前运行中的更新组件还是旧版本，请先重启应用后再切换更新源。');
        return;
      }
      const result = await window.codexWorkspace.setUpdateSourcePreference(nextPreference);
      setSourceSwitchMessage(result.message);
    } catch (error) {
      setSourceSwitchMessage(error instanceof Error ? error.message : '切换更新源失败。');
    } finally {
      setSourceSwitchingTo(null);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="shell-dialog-overlay" />
        <Dialog.Content
          className={`shell-dialog-panel shell-dialog-panel--update${
            notesExpanded ? ' is-notes-expanded' : ''
          }`}
          onPointerDownOutside={(event) => {
            if (isMandatory) {
              event.preventDefault();
            }
          }}
          onEscapeKeyDown={(event) => {
            if (isMandatory) {
              event.preventDefault();
            }
          }}
        >
          <header className="shell-dialog-header update-dialog__header">
            <div>
              <div className="update-dialog__title-row">
                <Dialog.Title
                  className={`shell-dialog-title update-dialog-title${
                    updateState.updateAvailable ? ' is-alert' : ''
                  }`}
                >
                  {dialogTitle}
                </Dialog.Title>
                {channelBadgeLabel ? (
                  <span className="update-dialog__channel-badge">{channelBadgeLabel}</span>
                ) : null}
              </div>
              <Dialog.Description className="sr-only">
                {dialogDescription}
              </Dialog.Description>
            </div>

            {!isMandatory ? (
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="shell-dialog-close shell-dialog-close--plain"
                  aria-label="关闭更新弹窗"
                >
                  <X size={18} />
                </button>
              </Dialog.Close>
            ) : null}
          </header>

          <div className={`shell-dialog-body update-dialog__body${notesExpanded ? ' is-notes-expanded' : ''}`}>
            {!notesExpanded ? (
              <section
                className={`update-dialog-banner${
                  updateState.updateAvailable ? ' is-alert' : isError ? ' is-error' : ''
                }`}
              >
                <div className="update-dialog-banner__icon">
                  <ShieldAlert size={20} />
                </div>
                <div className="update-dialog-banner__content">
                  <strong>{bannerTitle}</strong>
                  {bannerSummary ? <span>{bannerSummary}</span> : null}
                </div>
              </section>
            ) : null}

            {!notesExpanded && progressVisible ? (
              <section className="update-dialog-progress-card">
                <div className="update-dialog-copy__label">下载进度</div>
                <div className="update-dialog-progress-card__header">
                  <strong>{updateState.downloadStageLabel ?? '等待下载任务开始。'}</strong>
                  <span>{progressPercentLabel}</span>
                </div>
                <div
                  className={`update-dialog-progress-bar${
                    updateState.downloadProgressPercent === null && isDownloading ? ' is-indeterminate' : ''
                  }`}
                  aria-hidden="true"
                >
                  <div
                    className="update-dialog-progress-bar__fill"
                    style={
                      updateState.downloadProgressPercent !== null
                        ? { width: `${Math.max(updateState.downloadProgressPercent, 4)}%` }
                        : undefined
                    }
                  />
                </div>
              <div className="update-dialog-progress-card__meta">
                <span>{progressMetaLabel}</span>
              </div>
              </section>
            ) : null}

            {!notesExpanded ? (
              <section className="update-dialog__meta-grid">
                <div className="update-dialog__meta-item">
                  <span>当前版本</span>
                  <strong>{updateState.currentVersion}</strong>
                </div>
                <div className="update-dialog__meta-item">
                  <span>最新版本</span>
                  <strong>
                    {updateState.manifest?.version ?? (isUpToDate ? updateState.currentVersion : '未检查')}
                  </strong>
                </div>
                <div className="update-dialog__meta-item">
                  <span>发布时间</span>
                  <strong>{formatDateTime(updateState.manifest?.releaseTime)}</strong>
                </div>
                <div className="update-dialog__meta-item">
                  <span>当前状态</span>
                  <strong>{bannerTitle}</strong>
                </div>
              </section>
            ) : null}

            {!notesExpanded ? (
              <div className="update-dialog__controls">
                <section className="update-dialog__control-block">
                  <div className="update-dialog__control-title">更新来源</div>
                  <div className="update-dialog-source-actions update-dialog-source-actions--triple">
                    {(['auto', 'github', 'gitee'] as const).map((sourcePreference) => {
                      const active = updateState.sourcePreference === sourcePreference;
                      return (
                        <button
                          key={sourcePreference}
                          type="button"
                          className={active ? 'sidebar-button' : 'sidebar-button sidebar-button--secondary'}
                          onClick={() => void handleSwitchSourcePreference(sourcePreference)}
                          disabled={sourceControlsDisabled || active}
                        >
                          {mapUpdateSourcePreferenceLabel(sourcePreference)}
                        </button>
                      );
                    })}
                  </div>
                  {sourceSwitchMessage ? (
                    <span className="update-dialog-source-card__hint update-dialog-source-card__hint--visible">
                      {sourceSwitchMessage}
                    </span>
                  ) : null}
                </section>

                <section className="update-dialog__control-block">
                  <div className="update-dialog__control-title">常用入口</div>
                  <div className="update-dialog-source-actions update-dialog-source-actions--single">
                    <button
                      type="button"
                      className="sidebar-button sidebar-button--secondary"
                      onClick={() => onOpenExternal(officialDownloadUrl)}
                    >
                      官网下载
                    </button>
                  </div>
                </section>
              </div>
            ) : null}

            <section className="update-dialog-copy__section update-dialog-copy__section--notes">
              {!notesExpanded ? <div className="update-dialog-copy__label">更新说明</div> : null}
              <pre className={`update-dialog-notes${notesExpanded ? ' is-expanded' : ''}`}>{notesContent}</pre>
            </section>
          </div>

          <footer className="shell-dialog-footer update-dialog__footer">
            <div className="shell-dialog-footer__actions update-dialog__footer-actions">
              {!notesExpanded ? (
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={onCheckForUpdates}
                >
                  {busyAction === 'check' ? (
                    <RefreshCw size={15} className="spin" />
                  ) : (
                    <RefreshCw size={15} />
                  )}
                  检查更新
                </button>
              ) : null}
              <button
                type="button"
                className="sidebar-button sidebar-button--secondary"
                onClick={() => setNotesExpanded((current) => !current)}
              >
                <ArrowUpRight size={15} />
                {notesExpanded ? '收起完整说明' : '查看完整说明'}
              </button>
              {!notesExpanded ? (
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={onOpenHelp}
                >
                  联系客服
                </button>
              ) : null}
              {!isMandatory ? (
                <Dialog.Close asChild>
                  <button type="button" className="sidebar-button sidebar-button--secondary">
                    稍后再说
                  </button>
                </Dialog.Close>
              ) : null}
              <button
                type="button"
                className="sidebar-button"
                onClick={onInstallUpdate}
                disabled={!updateState.updateAvailable}
              >
                {busyAction === 'install' ? (
                  <RefreshCw size={15} className="spin" />
                ) : (
                  <Download size={15} />
                )}
                {primaryActionLabel}
              </button>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
